import { Icons } from '@/assets/icons/icons';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Text } from '@/components/ui/typography';
import { cn } from '@/lib/utils';

import { DownloadButton } from './download-button';

/**
 * Web-only replacement for the Meeting Notes setup checklist (which can't work
 * in the browser, since recording is desktop-only).
 *
 * - `banner`: sits above an existing list of notes.
 * - `empty`: fills the empty state when the user has no notes yet, with room
 *   for a fuller explanation.
 */
export const MeetingsDownloadCallout = ({
  variant,
}: {
  variant: 'banner' | 'empty';
}) => {
  if (variant === 'empty') {
    return (
      <YStack className="mx-auto max-w-xl items-center gap-5 py-16 text-center">
        <div className="flex size-14 items-center justify-center rounded-2xl bg-brand-jordy-blueA/15">
          <Icons.audio className="size-7 text-brand-jordy-blue" />
        </div>
        <YStack className="gap-2.5">
          <Text className="font-serif text-2xl">
            Record meetings with the desktop app
          </Text>
          <Text variant="subtle" className="text-[15px] leading-relaxed">
            Meeting Notes records your calls, transcribes them live, and writes
            an AI summary with action items. You'll need the desktop app to do
            this.
          </Text>
        </YStack>
        <DownloadButton variant="solid" size="lg" />
      </YStack>
    );
  }

  return (
    <XStack
      className={cn(
        'items-center justify-between gap-4 rounded-xl px-5 py-4',
        'border border-border bg-gradient-to-r from-brand-jordy-blueA/12 to-surface'
      )}
    >
      <XStack className="min-w-0 items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-jordy-blueA/20">
          <Icons.audio className="size-5 text-brand-jordy-blue" />
        </div>
        <YStack className="gap-0.5">
          <Text className="font-semibold">
            Record new meetings with the desktop app
          </Text>
          <Text size="sm" variant="subtle">
            Recording and live transcription aren&apos;t available in the
            browser. Install the desktop app to capture new meetings —
            they&apos;ll appear here automatically.
          </Text>
        </YStack>
      </XStack>
      <div className="shrink-0">
        <DownloadButton variant="solid" size="sm" />
      </div>
    </XStack>
  );
};
